// src/components/assets/UploadStep.tsx

import React from 'react';
import {
    Box,
    Button,
    styled,
    Tabs,
    Tab,
    useTheme,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent // <--- IMPORT THE CORRECT TYPE
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useAddAsset, UPLOAD_TYPES, ImageTypeKey } from '../../../context/AddAssetContext';
import FilePreviewList from './FilePreviewList';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadStep: React.FC = () => {
  const {
    currentImageType,
    setCurrentImageType,
    filesByType,
    addFiles,
    removeFile,
    isUploading,
    imageTags,
    setImageTag,
  } = useAddAsset();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files), currentImageType);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: ImageTypeKey) => {
    setCurrentImageType(newValue);
  };


  const handleSelectChange = (event: SelectChangeEvent) => {
    setCurrentImageType(event.target.value as ImageTypeKey);
  };
  
  return (
    <Box sx={{
      mt: 2,
      display: 'flex',
      flexDirection: 'column',
      // On mobile, the parent container in the modal handles height
      height: isMobile ? '100%' : '60vh'
    }}>

      {isMobile ? (
        // --- MOBILE VIEW ---
        <Box sx={{ p: 1 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="image-type-select-label">Image Category</InputLabel>
            <Select
              labelId="image-type-select-label"
              label="Image Category"
              value={currentImageType}
              onChange={handleSelectChange}
              disabled={isUploading}
            >
              {UPLOAD_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {type.toUpperCase()} ({filesByType[type].length})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            component="label"
            variant="contained"
            fullWidth // Make button take full width
            startIcon={<CloudUpload />}
            disabled={isUploading}
          >
            Upload {currentImageType.toUpperCase()}
            <VisuallyHiddenInput type="file" multiple accept="image/*,.heic,.heif,.mp4" onChange={handleFileSelect} />
          </Button>
        </Box>
      ) : (
        // --- DESKTOP VIEW ---
        <>
          <Box sx={{ flexShrink: 0, px: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentImageType}
              onChange={handleTabChange}
              textColor="inherit"
              aria-label="Image type tabs"
              variant="scrollable" // Allows tabs to scroll if they don't fit
              scrollButtons="auto" // Shows arrows if needed
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: 'error.main' },
                '& .MuiTab-root.Mui-selected': { color: 'error.main', fontWeight: 'bold' },
              }}
            >
              {UPLOAD_TYPES.map(type => (
                <Tab
                  key={type}
                  value={type}
                  label={`${type.toUpperCase()} (${filesByType[type].length})`}
                  disabled={isUploading}
                />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ flexShrink: 0, p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUpload />}
              disabled={isUploading}
            >
              Upload {currentImageType.toUpperCase()}
              <VisuallyHiddenInput type="file" multiple accept="image/*,.heic,.heif,.mp4" onChange={handleFileSelect} />
            </Button>
          </Box>
        </>
      )}

      {/* The FilePreviewList is shared between both views */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: isMobile ? 1 : 2 }}>
        <FilePreviewList
          files={filesByType[currentImageType]}
          onRemove={(fileName) => removeFile(fileName, currentImageType)}
          currentImageType={currentImageType}
          imageTags={imageTags[currentImageType]}
          onSetTag={(fileName, tag) => setImageTag(fileName, tag, currentImageType)}
        />
      </Box>
    </Box>
  );
};

export default UploadStep;